import { apiClient } from '../services/apiClient';

// ─── Types (mirror backend Content.Contracts DTOs — feature 017-playlist-curation) ───

/**
 * Playlist summary DTO as serialized by the Content module (camelCase).
 * Used for collection members and related-override entries. This is the same
 * shape the backend returns inside collection detail items and related state.
 */
export interface AudioPlaylistDto {
  id: string;
  organizationId: string | null;
  name: string;
  description: string | null;
  targetUseCase: string | null;
  thumbnailUrl: string | null;
  isPublished: boolean;
  trackCount: number;
  totalDurationSeconds: number;
  source: string;
  playlistType: string | null;
  createdByUserId: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  isLicensed: boolean;
  isLicensedOverride: boolean | null;
}

/** Admin summary of a playlist collection, including its item count. */
export interface PlaylistCollectionDto {
  id: string;
  slug: string;
  /** Required default title (platform default / English). */
  title: string;
  /** Locale→title map, e.g. {"uk": "Топ плейлисти"}. Always present (defaults to {}). */
  titles: Record<string, string>;
  displayOrder: number;
  isPublished: boolean;
  playlistCount: number;
  createdAt: string;
  updatedAt: string;
}

/** A member of a collection: the playlist, its position, and whether it is available. */
export interface PlaylistCollectionItemDto {
  playlist: AudioPlaylistDto;
  position: number;
  /** false when the member playlist is unpublished/deleted/no-longer-library. */
  isAvailable: boolean;
}

/** Admin detail of a collection with its ordered member playlists. */
export interface PlaylistCollectionDetailDto extends PlaylistCollectionDto {
  items: PlaylistCollectionItemDto[];
}

/** Current related state for a playlist: override (if any) + algorithmic preview. */
export interface RelatedOverrideStateDto {
  isOverridden: boolean;
  override: AudioPlaylistDto[];
  algorithmicPreview: AudioPlaylistDto[];
}

export interface CreateCollectionRequest {
  slug: string;
  /** Required default title. */
  title: string;
  /** Optional locale→title override map. */
  titles?: Record<string, string>;
}

export interface UpdateCollectionRequest {
  title?: string;
  /** Full replacement of the locale→title map when provided. */
  titles?: Record<string, string>;
  isPublished?: boolean;
}

// ─── Query keys ───────────────────────────────────────

export const collectionsQueryKey = ['admin-collections'] as const;
export const collectionQueryKey = (slug: string) => ['admin-collection', slug] as const;
export const relatedOverrideQueryKey = (playlistId: string) =>
  ['admin-related-override', playlistId] as const;

// ─── Collections ──────────────────────────────────────

export function fetchCollections(): Promise<PlaylistCollectionDto[]> {
  return apiClient.get<PlaylistCollectionDto[]>('/admin/playlist-collections');
}

export function createCollection(body: CreateCollectionRequest): Promise<PlaylistCollectionDto> {
  return apiClient.post<PlaylistCollectionDto>('/admin/playlist-collections', body);
}

/** Reorder the collections (full ordered slug list; position = array index). */
export function reorderCollections(slugs: string[]): Promise<void> {
  return apiClient.put<void>('/admin/playlist-collections/reorder', { slugs });
}

export function fetchCollection(slug: string): Promise<PlaylistCollectionDetailDto> {
  return apiClient.get<PlaylistCollectionDetailDto>(`/admin/playlist-collections/${slug}`);
}

export function updateCollection(
  slug: string,
  body: UpdateCollectionRequest,
): Promise<PlaylistCollectionDto> {
  return apiClient.patch<PlaylistCollectionDto>(`/admin/playlist-collections/${slug}`, body);
}

export function deleteCollection(slug: string): Promise<void> {
  return apiClient.delete<void>(`/admin/playlist-collections/${slug}`);
}

/** Set the collection membership + order (full replace; position = array index). */
export function setCollectionPlaylists(slug: string, playlistIds: string[]): Promise<void> {
  return apiClient.put<void>(`/admin/playlist-collections/${slug}/playlists`, { playlistIds });
}

// ─── Related overrides ────────────────────────────────

export function fetchRelatedOverride(playlistId: string): Promise<RelatedOverrideStateDto> {
  return apiClient.get<RelatedOverrideStateDto>(`/admin/curated-playlists/${playlistId}/related`);
}

/** Set or clear the related override (empty array clears → algorithm). */
export function setRelatedOverride(playlistId: string, relatedPlaylistIds: string[]): Promise<void> {
  return apiClient.put<void>(`/admin/curated-playlists/${playlistId}/related`, { relatedPlaylistIds });
}
